import React, { useCallback, useMemo, useEffect } from 'react';
import { getDataByPath } from '../../utils/dataHelpers';
import {
    isAutoWidget,
    isFindingListWidget,
    isCostSummaryWidget,
    getCostSummaryItemName,
    getDefaultUnitCost,
    getDefaultItemsPerPage,

    getAvailableFields,
    getNestedValueForFilters
} from '../../utils/widgetHelpers';
import { PAGINATED_WIDGETS } from '../../utils/constants';
import TableColumnConfiguration from './TableColumnConfiguration';
import TableSortConfiguration from './TableSortConfiguration';
import DeviceFiltersConfiguration from './DeviceFiltersConfiguration';
import TableFiltersConfiguration from './TableFiltersConfiguration';
import { getExtractDataObj } from '../../../widgets/TableDataMapper';
import SearchableSelect from '../SearchableSelect';

const isChartWidget = (widgetType) => 
    ['bar', 'line', 'pie', 'donut', 'radar', 'polar', 'bubble', 'scatter'].includes(widgetType);

const isStandardWidget = (widgetType) => 
    ['numeric', 'gauge', 'statcard', 'bar', 'line', 'pie', 'donut', 'radar', 'polar', 'bubble', 'scatter', 'table'].includes(widgetType);

const TABLE_WIDGET_COLUMNS = {
    // AD Tables
    'ad-users-audit-table': ['#', 'User', 'Hostname', 'Last Login', 'Last Password Set', 'Created', 'Flags', 'Status'],
    'ad-groups-risk-table': ['Status', 'Group Name', 'Members', 'Description', 'Action'],
    'ad-ous-risk-table': ['Status', 'OU Name', 'Distinguished Name', 'Action'],
    'ad-gpo-analysis-table': ['Status', 'GPO Name', 'GUID', 'Action'],
    'ad-fsmo-roles-table': ['Role', 'Owner', 'Status'],
    'ad-inactive-users-table': ['User', 'Last Login', 'Created', 'Status'],
    'ad-inactive-computers-table': ['Computer', 'Last Login', 'Created', 'Status'],
    'ad-computers-audit-table': ['#', 'Computer', 'Hostname', 'Operating System', 'OS Version', 'Last Login', 'Last Password Set', 'Created', 'Flags', 'Disabled'],
    'ad-devices-audit-table': ['#', 'Device Name', 'OS', 'OS Version', 'IP Address', 'Last Seen', 'Created', 'Enabled'],
    'ad-groups-audit-table': ['#', 'Group Name', 'Created', 'Distinguished Name', 'Security Group', 'Managed BY', 'Members', 'Description'],
    'ad-ous-audit-table': ['No.', 'OU', 'Distinguished Name', 'Members'],
    'ad-domain-controllers-audit-table': ['#', 'DC Name', 'IP Address', 'OS', 'Site', 'Roles'],
    'ad-gpo-policies-audit-table': ['#', 'GPO Name', 'GUID', 'Created', 'Modified', 'Status'],
    'ad-event-logs-audit-table': ['#', 'Timestamp', 'Event ID', 'Type', 'Source', 'Category', 'Record #', 'Message'],
    'ad-workstation-age-table': ['No.', 'Hostname', 'Last Logon', 'Added to Domain', 'Workstation Age (Months)'],
    // Network Tables
    'network-all-users-password-table': ['Username', 'Computer', 'Account Type', 'Password Required', 'Password Expires'],
    'network-non-expiring-passwords-table': ['No.', 'IP Address', 'Hostname', 'Username', 'Account Type', 'Password Required'],
    'network-admin-daily-work-table': ['No.', 'IP Address', 'Hostname', 'Group', 'Account'],
    'network-disk-usage-table': ['Device', 'Drive Letter', 'Disk Usage', 'Used (GB)', 'Total (GB)', 'Status'],
    'network-logical-disks-table': ['No.', 'Device', 'Computer Name', 'Drive Name', 'File System', 'Size (GB)', 'Used (GB)', 'Free (GB)', 'Disk Usage %'],
    'network-backup-status-table': ['Device', 'Hostname', 'Last Backup', 'Status', 'Notes'],
    'network-device-age-table': ['Device', 'Install Date', 'Age', 'Status'],
    'network-installed-software-table': ['Device', 'Name', 'Installed Version', 'Status'],
    'software-inventory-table': ['No.', 'IP', 'Hostname', 'Software Name', 'Publisher', 'Version/Build', 'Hostnames'],
    'local-administrators-table': ['No.', 'IP', 'HOSTNAME', 'LAST LOGIN', 'LOCAL ADMINISTRATOR'],
    'ad-admins-table': ['No.', 'ACCOUNT NAME', 'ACCOUNT TYPE', 'PRIVILEGED GROUP', 'ENABLED STATUS', 'LAST LOGON'],
    'network-os-versions-table': ['Device', 'OS Name', 'Version', 'Status'],
    'network-non-default-services-table': ['No.', 'IP Address', 'Hostname', 'Name', 'Display Name', 'State', 'Start Mode'],
    'network-all-open-ports-table': ['No.', 'IP Address', 'Hostname', 'Port', 'Is Sensitive', 'Service'],
    'network-sensitive-open-ports-table': ['No.', 'IP Address', 'Hostname', 'Port', 'Is Sensitive', 'Service'],
    'network-antivirus-status-table': ['No.', 'IP Address', 'Hostname', 'Antivirus', 'Path', 'Status'],
    'network-firewall-status-table': ['Device', 'Hostname', 'Firewall Profiles', 'Status'],
    'network-detailed-services-table': ['No.', 'IP Address', 'Hostname', 'Name', 'Display Name', 'State', 'Start Mode'],
    'network-services-risk-breakdown-table': ['No.', 'State', 'Total Services', 'Severity', 'Explanation'],
    'network-high-privilege-services-table': ['No.', 'IP Address', 'Hostname', 'Name', 'Display Name', 'Reason', 'Risk', 'Note'],
    'network-users-no-expiring-password-table': ['No.', 'IP Address', 'Hostname', 'Username', 'Account Type', 'Password Required'],
    'network-users-expiring-password-table': ['No.', 'IP Address', 'Hostname', 'Username', 'Account Type', 'Password Required'],
    'network-admins-no-expiring-password-table': ['No.', 'IP Address', 'Hostname', 'Username', 'Account Type', 'Password Required'],
    'network-admins-expiring-password-table': ['No.', 'IP Address', 'Hostname', 'Username', 'Account Type', 'Password Required'],
    'network-admins-daily-work-table': ['No.', 'IP Address', 'Hostname', 'Group', 'Account'],
    'network-encryption-status-table': ['No.', 'IP Address', 'Hostname', 'Services', 'Status'],
    // Localhost Tables
    'localhost-all-users-password-table': ['No.', 'Username', 'Computer', 'Account Type', 'Password Required', 'Password Expires'],
    'localhost-non-expiring-passwords-table': ['No.', 'IP Address', 'Hostname', 'Username', 'Account Type', 'Password Required'],
    'localhost-disk-usage-table': ['No.', 'Device', 'Drive Letter', 'Disk Usage', 'Used (GB)', 'Total (GB)', 'Status'],
    'localhost-firewall-status-table': ['IP Address', 'Hostname', 'Firewall Profiles', 'Status'],
    'localhost-os-version-table': ['No.', 'OS Name', 'Count', 'Host', 'Status'],
    'localhost-sensitive-open-ports-table': ['No.', 'IP Address', 'Hostname', 'Port', 'Service'],
    'localhost-services-risk-breakdown-table': ['No.', 'State', 'Total Services', 'Severity', 'Explanation'],
    'localhost-high-privilege-services-table': ['No.', 'IP Address', 'Hostname', 'Name', 'Display Name', 'Reason', 'Risk', 'Note'],
    'localhost-installed-software-table': ['No.', 'Device', 'Name', 'Installed Version', 'Status'],
    'localhost-non-default-services-table': ['No.', 'IP Address', 'Hostname', 'Name', 'Display Name', 'Reason', 'State', 'Start Mode'],
    'localhost-open-ports-table': ['No.', 'IP Address', 'Hostname', 'Port', 'Is Sensitive', 'Service'],
    'localhost-browser-passwords-table': ['No.', 'IP Address', 'Hostname', 'Username', 'URL', 'Strength', 'Reuse Count', 'Password'],
    'localhost-hardware-information-table': ['No.', 'Component', 'Details'],
    'localhost-system-information-table': ['No.', 'Property', 'Value'],
    'localhost-installed-applications-table': ['No.', 'Name', 'Version', 'Publisher'],
    'localhost-event-logs-table': ['No.', 'Event ID', 'Source', 'Level', 'Message', 'Date'],
    'localhost-open-ports-audit-table': ['No.', 'IP Address', 'Hostname', 'Device Name', 'Open Ports', 'Sensitive Port'],
    // MS365 Tables
    'ms365-inactive-users-table': ['No.', 'User Name', 'User ID'],
    'ms365-all-users-mfa-table': ['No.', 'Display Name', 'MFA Status', 'Authentication'],
    'ms365-users-without-mfa-table': ['No.', 'Display Name', 'MFA Status', 'Authentication'],
    'ms365-policy-config-table': ['No.', 'Policy Name', 'Description', 'Status'],
    'ms365-scan-proof-table': ['No.', 'Property', 'Value'],
    'microsoft-policies-table': ['No.', 'Policy Name', 'Description', 'Status'],
    // HIBP/Dark Web Tables
    'hibp-breached-credentials-table': ['No.', 'User Email', 'Breach Source', 'Password', 'Exposure Date', 'Reuse'],
    'hibp-exposure-by-platform-table': ['Platform', 'Credentials Found'],
    // PII Tables
    'pii-detected-table': ['No.', 'Type', 'Value', 'Source'],
    'pii-type-summary-table': ['Type', 'Total Detected'],
    // External Risk Scan Tables
    'external-all-findings-table': ['No.', 'Category', 'Finding', 'Status', 'Details'],
    'external-warnings-table': ['No.', 'Category', 'Finding', 'Details'],
    'external-passed-table': ['No.', 'Category', 'Finding'],
    'external-compliance-table': ['No.', 'Standard', 'Requirement', 'Status'],
    'external-dns-findings': ['No.', 'Finding', 'Status', 'Details'],
    'external-email-findings': ['No.', 'Finding', 'Status', 'Details'],
    'external-ssl-findings': ['No.', 'Finding', 'Status', 'Details'],
    'external-headers-findings': ['No.', 'Finding', 'Status', 'Details'],
    'external-infrastructure-findings': ['No.', 'Finding', 'Status', 'Details'],
    'external-breach-findings': ['No.', 'Finding', 'Status', 'Details'],
};

const DataTab = ({
    block,
    scanData,
    dataContext,
    availableMetrics,
    onDataConfigUpdate,
    onConfigUpdate,
    pages
}) => {
    // Helper to update data config
    const updateDataConfig = (key, value) => {
        if (block) {
            // If updating customUnitCosts, merge with existing values
            if (key === 'customUnitCosts') {
                const currentCustomUnitCosts = block.dataConfig?.customUnitCosts || {};
                onDataConfigUpdate(block.id, {
                    ...block.dataConfig,
                    customUnitCosts: {
                        ...currentCustomUnitCosts,
                        ...value
                    }
                });
            } else {
                onDataConfigUpdate(block.id, { [key]: value });
            }
        }
    };

    // Helper to update widget config (block.config)
    const updateWidgetConfig = (key, value) => {
        if (block && onConfigUpdate) {
            onConfigUpdate(block.id, { [key]: value });
        }
    };

    // Stable callback for updating table columns
    const blockId = block?.id;
    const handleUpdateTableColumns = useCallback((newColumns) => {
        console.log('[DataTab] handleUpdateTableColumns called:', { blockId, columnsCount: newColumns.length, newColumns });
        if (blockId) {
            onDataConfigUpdate(blockId, { tableColumns: newColumns });
        } else {
            console.warn('[DataTab] No blockId for column update');
        }
    }, [blockId, onDataConfigUpdate]);

    // Stable callback for updating device filters
    const handleUpdateDeviceFilters = useCallback((newFilters) => {
        console.log('[DataTab] handleUpdateDeviceFilters called:', { blockId, filtersCount: newFilters.length, newFilters });
        if (blockId) {
            onDataConfigUpdate(blockId, { deviceFilters: newFilters });
        } else {
            console.warn('[DataTab] No blockId for filter update');
        }
    }, [blockId, onDataConfigUpdate]);

    // Stable callback for updating table filters
    const handleUpdateTableFilters = useCallback((newFilters) => {
        const count = Array.isArray(newFilters) ? newFilters.length : (newFilters?.filters?.length || 0);
        console.log('[DataTab] handleUpdateTableFilters called:', { blockId, filtersCount: count, newFilters });
        if (blockId) {
            onDataConfigUpdate(blockId, { tableFilters: newFilters });
        } else {
            console.warn('[DataTab] No blockId for table filter update');
        }
    }, [blockId, onDataConfigUpdate]);

    // Stable callback for updating table sort
    const handleUpdateTableSort = useCallback((newSort) => {
        console.log('[DataTab] handleUpdateTableSort called:', { blockId, newSort });
        if (blockId) {
            onDataConfigUpdate(blockId, { 
                tableSort: newSort,
                sortField: newSort?.fieldPath || '',
                sortDirection: newSort?.direction || 'asc'
            });
        } else {
            console.warn('[DataTab] No blockId for table sort update');
        }
    }, [blockId, onDataConfigUpdate]);

    // Filter metrics based on widget type
    const getFilteredMetrics = () => {
        if (!block || block.type !== 'widget') return availableMetrics;

        const widgetType = block.widgetType;

        // Define which metric types are compatible with each widget
        const metricTypeMap = {
            numeric: ['number'],
            gauge: ['number'],
            statcard: ['number'],
            bar: ['array', 'object'],
            line: ['array', 'object'],
            pie: ['array', 'object'],
            donut: ['array', 'object'],
            radar: ['array', 'object'],
            polar: ['array', 'object'],
            bubble: ['array', 'object'],
            scatter: ['array', 'object'],
            table: ['array', 'object']
        };

        const allowedTypes = metricTypeMap[widgetType] || ['number', 'array', 'object'];
        
        // Initial type-based filtering
        const typeFiltered = availableMetrics.filter(metric => allowedTypes.includes(metric.type));

        // Content-based filtering: For charts, exclude data that has NO numeric fields
        // This prevents users from selecting "network_devices" (list of objects) for a Bar Chart
        // if that list doesn't contain any numbers to plot.
        const isChartWidget = ['bar', 'line', 'pie', 'donut', 'radar', 'polar', 'bubble', 'scatter'].includes(widgetType);
        
        let contentFiltered = typeFiltered;
        if (isChartWidget) {
            contentFiltered = typeFiltered.filter(metric => {
                // Always allow explicitly numeric metrics
                if (metric.type === 'number') return true;

                // For array/object, check if the data actually contains numbers
                // We use the dataContext to peek at the data
                const data = getDataByPath(dataContext, metric.path);
                
                // If no data, keep it (might be populated later)
                if (!data) return true;

                // If array of objects, check for at least one numeric field
                if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
                    const sample = data[0];
                    // Check if any value in the object is a number or parseable string number
                    const hasNumber = Object.values(sample).some(val => 
                        typeof val === 'number' || 
                        (typeof val === 'string' && !isNaN(parseFloat(val)) && isFinite(parseFloat(val)))
                    );
                    return hasNumber;
                }
                
                // If object (not array), check values
                if (typeof data === 'object' && !Array.isArray(data)) {
                    const values = Object.values(data);
                    return values.some(val => typeof val === 'number');
                }

                return true;
            });
        }

        // Filter by specific allowed categories for Standard Charts & Tables as requested by user
        if (isStandardWidget(widgetType)) {
            const allowedLabels = [
                'Internet Speed',
                'Pipsqueak',
                'Pisqueak',
                'Localhost Scan',
                'Network Scan',
                'AD Scan',
                'MS365 Scan',
                'PII Scan'
            ];

            const labelMap = {
                'network devices': 'Network Scan',
                'network devices (all values)': 'Network Scan (All Values)',
                'localhost': 'Localhost Scan',
                'ad scan': 'AD Scan',
                'pisqueak': 'Pipsqueak'
            };

            contentFiltered = contentFiltered
                .map(metric => {
                    const lowerLabel = metric.label.toLowerCase();
                    return {
                        ...metric,
                        label: labelMap[lowerLabel] || metric.label
                    };
                })
                .filter(metric => {
                    const lowerLabel = metric.label.toLowerCase();
                    // Filter by specific allowed labels
                    const isAllowed = allowedLabels.some(allowed => {
                        const lowerAllowed = allowed.toLowerCase();
                        return lowerLabel === lowerAllowed || lowerLabel.startsWith(`${lowerAllowed} (`);
                    });
                    if (!isAllowed) return false;

                    // Specifically for Network Scan, if it's an object, we want the array instead
                    if (metric.label === 'Network Scan' && metric.type === 'object') {
                        // Check if there's also an array version in the list
                        const hasArrayVersion = contentFiltered.some(m => m.label === 'Network Scan' && m.type === 'array');
                        if (hasArrayVersion) return false;
                        return false; 
                    }

                    return true;
                });
        }

        // Deduplicate by label
        const seen = new Map();
        contentFiltered.forEach(metric => {
            // Prefer Array over Object if labels are same
            const existing = seen.get(metric.label);
            if (!existing || (existing.type !== 'array' && metric.type === 'array')) {
                seen.set(metric.label, metric);
            }
        });
        return Array.from(seen.values());
    };



    const filteredMetrics = getFilteredMetrics();

    // --- NEW: Calculate Chart Fields Hook ---
    const chartFieldData = useMemo(() => {
        if (!block.dataConfig.metricPath) return null;
        
        const data = getDataByPath(dataContext, block.dataConfig.metricPath);
        const isArray = Array.isArray(data) && data.length > 0 && typeof data[0] === 'object';
        const isChartWidget = ['bar', 'line', 'pie', 'donut', 'radar', 'polar', 'bubble', 'scatter'].includes(block.widgetType);
        
        if (!isArray || !isChartWidget) return null;

        const availableFields = getAvailableFields(scanData || dataContext, block.dataConfig.metricPath);
        const numericFields = availableFields.filter(field => {
            if (!data || data.length === 0) return false;
            const sample = data[0];
            const value = sample[field];
            // STRICTER CHECK: Use Number() instead of parseFloat() to avoid "2025-01-01" becoming 2025
            // Also explicitly check for date-like strings
            if (typeof value === 'number') return true;
            if (typeof value === 'boolean') return true; // Booleans can be aggregated (True=1, False=0)
            if (typeof value === 'string') {
                 // Reject if it looks like a date (ISO format or common separators)
                 if (value.includes('-') || value.includes(':') || value.includes('T')) return false;
                 return !isNaN(Number(value)) && isFinite(Number(value)) && value.trim() !== '';
            }
            return false;
        });
        // Added boolean fields for completeness - scan top 10 for better detection
        const booleanFields = availableFields.filter(field => {
             if (!data || data.length === 0) return false;
             // Check first 10 items for any boolean value
             return data.slice(0, 10).some(item => item && typeof item[field] === 'boolean');
        });
        const textFields = availableFields.filter(field => {
             if (!data || data.length === 0) return false;
             const sample = data[0];
             const value = sample[field];
             // Allow ANY string that is not a strict number, or is explicitly a date-like string
             if (typeof value === 'string') {
                 // If it's a date-like string (has separators), it's definitely text/label
                 if (value.includes('-') || value.includes(':') || value.includes('T')) return true;
                 
                 // Otherwise, if it's not a valid strict number, it's text
                 // (Use opposite check of numericFields)
                 return isNaN(Number(value)) || !isFinite(Number(value)) || value.trim() === '';
             }
             return false;
        });
        const categoryFields = availableFields.filter(field => {
             // Boolean fields are always good categories
             if (booleanFields.includes(field)) return true;
             
             const uniqueValues = new Set();
             data.slice(0, 50).forEach(item => { // Scan more items (50 instead of 20)
                 if (item && item[field] !== undefined && item[field] !== null) {
                    uniqueValues.add(String(item[field]));
                 }
             });
             return uniqueValues.size >= 2 && uniqueValues.size <= 20; // Allow more categories (20 instead of 10)
        });
        
        return {
            availableFields,
            numericFields,
            booleanFields,
            textFields,
            categoryFields,
            data
        };
    }, [block.dataConfig.metricPath, block.widgetType, dataContext, scanData]);

    // --- NEW: Auto-select Default Axes Effect ---
    useEffect(() => {
        if (chartFieldData && blockId) {
             const { textFields, numericFields, availableFields } = chartFieldData;
             
             // Auto-select Label Field (X-axis)
             if (!block.dataConfig.labelField) { // If empty (formerly Auto-detect)
                  // Default to first text field, or first available field
                  const defaultLabel = textFields.length > 0 ? textFields[0] : (availableFields.length > 0 ? availableFields[0] : '');
                  if (defaultLabel) {
                       console.log('[DataTab] Auto-selecting labelField:', defaultLabel);
                       updateDataConfig('labelField', defaultLabel);
                  }
             }
             
             // Auto-select Value Field (Y-axis)
             if (!block.dataConfig.valueField) { // If empty (formerly Auto-detect)
                  // Default to first numeric field, or first available
                  const defaultValue = numericFields.length > 0 ? numericFields[0] : (availableFields.length > 0 ? availableFields[0] : '');
                  if (defaultValue) {
                       console.log('[DataTab] Auto-selecting valueField:', defaultValue);
                       updateDataConfig('valueField', defaultValue);
                  }
             }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartFieldData, blockId, block.dataConfig.labelField, block.dataConfig.valueField]); 

    // --- NEW: Render Segments Configuration ---
    const renderSegmentsConfiguration = () => {
        const segments = block.dataConfig.segments || [];

        const addSegment = () => {
            const newSegment = {
                id: `seg-${Date.now()}`,
                label: `Segment ${segments.length + 1}`,
                color: '#3b82f6', // Default blue
                filters: []
            };
            updateDataConfig('segments', [...segments, newSegment]);
        };

        const updateSegment = (segmentId, updates) => {
            updateDataConfig('segments', segments.map(s => 
                s.id === segmentId ? { ...s, ...updates } : s
            ));
        };

        const removeSegment = (segmentId) => {
            updateDataConfig('segments', segments.filter(s => s.id !== segmentId));
        };

        return (
            <div className="property-field" style={{ marginTop: '1rem', borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
                <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                    📊 Chart Segments
                </label>
                <div style={{ marginBottom: '1rem', fontSize: '0.875rem', color: '#666' }}>
                    Define segments to count items matching specific criteria. Each segment becomes a slice/bar in the chart.
                </div>

                {segments.map((segment, index) => (
                    <div key={segment.id} style={{ 
                        border: '1px solid #e5e7eb', 
                        borderRadius: '6px', 
                        marginBottom: '1rem',
                        overflow: 'hidden'
                    }}>
                        <div style={{ 
                            padding: '0.75rem', 
                            backgroundColor: '#f9fafb', 
                            borderBottom: '1px solid #e5e7eb',
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between' 
                        }}>
                            <div style={{ fontWeight: '600', fontSize: '0.9rem' }}>Segment {index + 1}</div>
                            <button 
                                onClick={() => removeSegment(segment.id)}
                                style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.25rem' }}
                            >
                                ×
                            </button>
                        </div>
                        <div style={{ padding: '0.75rem' }}>
                            <div className="property-field">
                                <label>Label</label>
                                <input 
                                    type="text" 
                                    value={segment.label} 
                                    onChange={(e) => updateSegment(segment.id, { label: e.target.value })}
                                    placeholder="e.g. Has Antivirus"
                                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                                />
                            </div>
                            <div className="property-field">
                                <label>Color</label>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <input 
                                        type="color" 
                                        value={segment.color || '#3b82f6'} 
                                        onChange={(e) => updateSegment(segment.id, { color: e.target.value })}
                                        style={{ width: '40px', height: '38px', padding: '0', border: 'none', cursor: 'pointer' }}
                                    />
                                    <input 
                                        type="text" 
                                        value={segment.color || '#3b82f6'} 
                                        onChange={(e) => updateSegment(segment.id, { color: e.target.value })}
                                        style={{ flex: 1, padding: '0.5rem', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                                    />
                                </div>
                            </div>
                            
                            <DeviceFiltersConfiguration 
                                block={block}
                                scanData={scanData}
                                dataContext={dataContext}
                                filters={segment.filters || []}
                                onUpdateFilters={(newFilters) => updateSegment(segment.id, { filters: newFilters })}
                            />
                        </div>
                    </div>
                ))}

                <button 
                    onClick={addSegment}
                    style={{
                        width: '100%',
                        padding: '0.75rem',
                        backgroundColor: '#eff6ff',
                        color: '#3b82f6',
                        border: '1px dashed #3b82f6',
                        borderRadius: '6px',
                        fontWeight: '600',
                        cursor: 'pointer'
                    }}
                >
                    + Add Segment
                </button>
            </div>
        );
    };

    if (block.type !== 'widget') return null;

    return (
        <div className="property-group">
            <h4>Data Configuration</h4>
            
            {block.widgetType === 'network-firewall-status-chart' && (
                <div className="property-field">
                    <label>Chart Type</label>
                    <select
                        value={block.config?.chartType || 'donut'}
                        onChange={(e) => updateWidgetConfig('chartType', e.target.value)}
                    >
                        <option value="pie">Pie Chart</option>
                        <option value="donut">Donut Chart</option>
                        <option value="bar">Bar Chart (Horizontal)</option>
                        <option value="line">Line Chart</option>
                    </select>
                </div>
            )}
            {/* Only show Data Source field for non-auto widgets and exclude Standard Widgets as requested */}
            {!isAutoWidget(block.widgetType) && !isStandardWidget(block.widgetType) && (
                <div className="property-field">
                    <label>Data Source</label>
                    <SearchableSelect
                        value={block.dataConfig.metricPath || ''}
                        onChange={(newValue) => {
                            updateDataConfig('metricPath', newValue);
                            // Reset field selections when data source changes
                            updateDataConfig('labelField', '');
                            updateDataConfig('valueField', '');
                            // Reset table columns when data source changes
                            if (block.widgetType === 'table') {
                                updateDataConfig('tableColumns', []);
                            }
                        }}
                        placeholder="Select a metric..."
                        options={filteredMetrics.map(metric => ({
                            label: `${metric.category} - ${metric.label} (${metric.type})`,
                            value: metric.path
                        }))}
                    />
                </div>
            )}

            {/* Field Selection for Array-based Metrics */}
            {chartFieldData && (() => {
                 const { 
                     numericFields, booleanFields, textFields, categoryFields, availableFields, data 
                 } = chartFieldData;
                 const hasNoNumericValues = numericFields.length === 0;
                 const needsGrouping = hasNoNumericValues && !block.dataConfig.filterMode;

                 return (
                     <>
                        {/* Smart suggestion for data without numeric values */}
                        {needsGrouping && (
                            <div style={{
                                padding: '0.75rem',
                                backgroundColor: '#fef3c7',
                                borderRadius: '6px',
                                border: '1px solid #f59e0b',
                                marginBottom: '1rem'
                            }}>
                                <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <span>💡</span> This data has no numeric values
                                </div>
                                <div style={{ fontSize: '0.875rem', color: '#78350f', marginBottom: '0.75rem' }}>
                                    For best results with this data, use <strong>Group by Field</strong> to show counts.
                                    Good fields to group by:
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                    {[...booleanFields, ...categoryFields.filter(f => !booleanFields.includes(f))].slice(0, 5).map(field => (
                                        <button
                                            key={field}
                                            onClick={() => {
                                                updateDataConfig('aggregation', 'count');
                                                updateDataConfig('filterMode', 'group');
                                                updateDataConfig('filterField', field);
                                                updateDataConfig('labelField', field);
                                            }}
                                            style={{
                                                padding: '0.25rem 0.75rem',
                                                fontSize: '0.8rem',
                                                backgroundColor: '#fff',
                                                border: '1px solid #7dd3fc',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                color: '#0369a1',
                                                fontWeight: '500'
                                            }}
                                        >
                                            Count occurrences of "{field}"
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                                 <div className="property-field">
                                    <label>Aggregation</label>
                                    <select
                                        value={block.dataConfig.aggregation || 'count'}
                                        onChange={(e) => {
                                            const agg = e.target.value;
                                            updateDataConfig('aggregation', agg);
                                            
                                            // Handle special modes
                                            if (agg === 'count_segments') {
                                                updateDataConfig('filterMode', 'none');
                                                // Initialize segments if empty
                                                if (!block.dataConfig.segments || block.dataConfig.segments.length === 0) {
                                                     updateDataConfig('segments', [
                                                         { id: `seg-${Date.now()}-1`, label: 'Segment 1', color: '#3b82f6', filters: [] },
                                                         { id: `seg-${Date.now()}-2`, label: 'Segment 2', color: '#ef4444', filters: [] }
                                                     ]);
                                                }
                                            } else if (agg !== 'direct') {
                                                // Grouping modes (count, sum, avg)
                                                updateDataConfig('filterMode', 'group');
                                                // If no filterField, try to use labelField
                                                if (!block.dataConfig.filterField && block.dataConfig.labelField) {
                                                    updateDataConfig('filterField', block.dataConfig.labelField);
                                                }
                                            } else {
                                                // Direct mode
                                                updateDataConfig('filterMode', 'none');
                                            }
                                        }}
                                    >
                                        <option value="direct">Direct Value (from data)</option>
                                        <option value="count">Count occurrences</option>
                                        <option value="count_segments">Count Segments (Conditional)</option>
                                        <option value="sum">Sum of field</option>
                                        <option value="average">Average of field</option>
                                    </select>
                                    <small className="help-text" style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
                                        {block.dataConfig.aggregation === 'direct' 
                                            ? 'Plot numeric values exactly as they appear in the data.' 
                                            : block.dataConfig.aggregation === 'count_segments'
                                                ? 'Define custom segments with filters to count matching items.'
                                                : `Automatically groups by "${block.dataConfig.filterField || 'the selected category'}" and calculates the ${block.dataConfig.aggregation || 'count'}.`}
                                    </small>
                                </div>

                                {block.dataConfig.aggregation === 'count_segments' && renderSegmentsConfiguration()}

                                {block.dataConfig.aggregation !== 'direct' && (
                                    <div className="property-field">
                                        <label>Group By Field (Categories)</label>
                                        <SearchableSelect
                                            value={block.dataConfig.filterField || ''}
                                            onChange={(newValue) => {
                                                updateDataConfig('filterField', newValue);
                                                updateDataConfig('labelField', newValue); // Sync labelField for charts
                                            }}
                                            placeholder="Select category field..."
                                            options={availableFields.map(field => ({
                                                label: field,
                                                value: field
                                            }))}
                                        />
                                    </div>
                                )}

                                    {/* Special Field Selection for Bubble/Scatter Charts */}
                                    {block.dataConfig.aggregation === 'direct' && ['bubble', 'scatter'].includes(block.widgetType) ? (
                                        <>
                                            <div className="property-field">
                                                <label>X-coordinate Field (Horizontal)</label>
                                                <SearchableSelect
                                                    value={block.dataConfig.xField || ''}
                                                    onChange={(newValue) => updateDataConfig('xField', newValue)}
                                                    placeholder="Select X-axis field..."
                                                    options={[
                                                        ...numericFields.map(field => ({ label: `${field} (numeric)`, value: field })),
                                                        ...(numericFields.length === 0 ? availableFields.map(field => ({ label: field, value: field })) : [])
                                                    ]}
                                                />
                                                <small className="help-text" style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
                                                    Numeric field for the horizontal axis.
                                                </small>
                                            </div>

                                            <div className="property-field">
                                                <label>Y-coordinate Field (Vertical)</label>
                                                <SearchableSelect
                                                    value={block.dataConfig.yField || ''}
                                                    onChange={(newValue) => updateDataConfig('yField', newValue)}
                                                    placeholder="Select Y-axis field..."
                                                    options={[
                                                        ...numericFields.map(field => ({ label: `${field} (numeric)`, value: field })),
                                                        ...(numericFields.length === 0 ? availableFields.map(field => ({ label: field, value: field })) : [])
                                                    ]}
                                                />
                                                <small className="help-text" style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
                                                    Numeric field for the vertical axis.
                                                </small>
                                            </div>

                                            {block.widgetType === 'bubble' && (
                                                <div className="property-field">
                                                    <label>Bubble Size (Radius)</label>
                                                    <SearchableSelect
                                                        value={block.dataConfig.radiusField || ''}
                                                        onChange={(newValue) => updateDataConfig('radiusField', newValue)}
                                                        placeholder="Select size field..."
                                                        options={[
                                                            ...numericFields.map(field => ({ label: `${field} (numeric)`, value: field })),
                                                            ...(numericFields.length === 0 ? availableFields.map(field => ({ label: field, value: field })) : [])
                                                        ]}
                                                    />
                                                    <small className="help-text" style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
                                                        Numeric field to determine bubble size.
                                                    </small>
                                                </div>
                                            )}

                                            <div className="property-field">
                                                <label>Label Field (Tooltips)</label>
                                                <SearchableSelect
                                                    value={block.dataConfig.labelField || ''}
                                                    onChange={(newValue) => updateDataConfig('labelField', newValue)}
                                                    placeholder="Select label..."
                                                    options={[
                                                        ...textFields.map(field => ({ label: field, value: field })),
                                                        ...(textFields.length === 0 ? availableFields.map(field => ({ label: field, value: field })) : [])
                                                    ]}
                                                />
                                                <small className="help-text" style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
                                                    Field to display when hovering over a data point.
                                                </small>
                                            </div>

                                            {block.widgetType === 'bubble' && (
                                                <div style={{
                                                    padding: '0.75rem',
                                                    backgroundColor: '#f0fdf4',
                                                    borderRadius: '6px',
                                                    border: '1px solid #86efac',
                                                    marginTop: '1rem',
                                                    fontSize: '0.85rem'
                                                }}>
                                                    <div style={{ fontWeight: '600', color: '#166534', marginBottom: '0.25rem' }}>
                                                        💡 Best Example Usage
                                                    </div>
                                                    <div style={{ color: '#14532d' }}>
                                                        Try plotting <strong>RAM Used</strong> vs <strong>CPU Available</strong> with bubble size as <strong>Open Ports</strong>.
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            {(block.dataConfig.aggregation === 'direct' || ['sum', 'average'].includes(block.dataConfig.aggregation)) && (
                                                <div className="property-field">
                                                    <label>
                                                        {block.dataConfig.aggregation === 'direct' ? 'Value Field (Y-axis)' : 'Field to Aggregate'}
                                                    </label>
                                                    <SearchableSelect
                                                        value={block.dataConfig.valueField || ''}
                                                        onChange={(newValue) => updateDataConfig('valueField', newValue)}
                                                        placeholder="Select value field..."
                                                        options={[
                                                            ...numericFields.map(field => ({ label: `${field} (numeric)`, value: field })),
                                                            ...(numericFields.length === 0 ? availableFields.map(field => ({ label: field, value: field })) : [])
                                                        ]}
                                                    />
                                                    <small className="help-text" style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
                                                        Field to use for numeric calculations.
                                                    </small>
                                                </div>
                                            )}

                                            {block.dataConfig.aggregation === 'direct' && (
                                                <div className="property-field">
                                                    <label>Label Field (X-axis/Categories)</label>
                                                    <SearchableSelect
                                                        value={block.dataConfig.labelField || ''}
                                                        onChange={(newValue) => updateDataConfig('labelField', newValue)}
                                                        placeholder="Select label..."
                                                        options={[
                                                            ...textFields.map(field => ({ label: field, value: field })),
                                                            ...(textFields.length === 0 ? availableFields.map(field => ({ label: field, value: field })) : [])
                                                        ]}
                                                    />
                                                    <small className="help-text" style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
                                                        Field to use for chart labels/categories
                                                    </small>
                                                </div>
                                            )}
                                        </>
                                    )}
                            </>
                 );
             })()}

             {/* Hide Zero Values Option - Chart only */}
            {isChartWidget(block.widgetType) && !block.dataConfig.filterMode && (
                <div className="property-field" style={{ marginTop: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <input
                            type="checkbox"
                            checked={block.dataConfig.hideZeroValues || false}
                            onChange={(e) => updateDataConfig('hideZeroValues', e.target.checked)}
                            style={{ marginRight: '0.5rem' }}
                        />
                        <label style={{ marginBottom: 0 }}>Hide Zero Values</label>
                    </div>
                    <small className="help-text" style={{ display: 'block', marginLeft: '1.5rem', color: '#666' }}>
                        Exclude items where value is 0
                    </small>
                </div>
            )}

            {/* Quick Filter/Exclude UI */}
            {(block.dataConfig.labelField || block.dataConfig.filterField) && (
                <div className="property-field" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                    <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                        Include/Exclude Items
                    </label>
                    <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '4px', padding: '0.5rem' }}>
                        {(() => {
                            const data = chartFieldData ? chartFieldData.data : getDataByPath(dataContext, block.dataConfig.metricPath);
                            const field = block.dataConfig.filterMode === 'group' 
                                ? block.dataConfig.filterField 
                                : block.dataConfig.labelField;
                            
                            // 1. Get unique values and check if field is an array type
                            const uniqueItems = new Set();
                            let isArrayField = false;

                            if (data && Array.isArray(data)) {
                                data.forEach(item => {
                                    if (item && typeof item === 'object') {
                                        const val = getNestedValueForFilters(item, field);
                                        if (val !== undefined && val !== null) {
                                            if (Array.isArray(val)) {
                                                isArrayField = true;
                                                val.forEach(v => uniqueItems.add(String(v)));
                                            } else if (typeof val === 'object') {
                                                uniqueItems.add(JSON.stringify(val));
                                            } else {
                                                uniqueItems.add(String(val));
                                            }
                                        }
                                    }
                                });
                            }
                            
                            // 2. Render Checkboxes
                            const items = Array.from(uniqueItems).sort().slice(0, 50); // Limit to 50
                            
                            return items.length > 0 ? items.map(val => {
                                // Determine expected operator based on field type
                                const excludeOperator = isArrayField ? 'not_contains' : 'not_equals';

                                // Check if this value is currently excluded via deviceFilters
                                const isExcluded = (block.dataConfig.deviceFilters || []).some(f => 
                                    f.fieldPath === field && 
                                    f.operator === excludeOperator && 
                                    String(f.value) === val
                                );

                                // Determine Display Label
                                let displayLabel = val;
                                if (!val || val === '' || val === 'null' || val === 'undefined') {
                                    displayLabel = '(Empty)';
                                } else if (val === '[object Object]') {
                                    displayLabel = '(Not Empty/Has Value)';
                                }
                                
                                return (
                                    <div key={val} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}>
                                        <input 
                                            type="checkbox"
                                            checked={!isExcluded}
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                const currentFilters = block.dataConfig.deviceFilters || [];
                                                let newFilters;
                                                
                                                if (!checked) {
                                                    // Exclude: Add filter
                                                    newFilters = [...currentFilters, {
                                                        id: `exclude-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
                                                        fieldPath: field,
                                                        operator: excludeOperator,
                                                        value: val,
                                                        logic: 'AND'
                                                    }];
                                                } else {
                                                    // Include: Remove specific filter
                                                    newFilters = currentFilters.filter(f => 
                                                        !(f.fieldPath === field && f.operator === excludeOperator && String(f.value) === val)
                                                    );
                                                }
                                                updateDataConfig('deviceFilters', newFilters);
                                            }}
                                            style={{ marginRight: '0.5rem' }}
                                        />
                                        <span style={{ fontSize: '0.9rem', color: isExcluded ? '#999' : '#000' }}>
                                            {displayLabel}
                                        </span>
                                    </div>
                                );
                            }) : (
                                <div style={{ color: '#666', fontSize: '0.875rem' }}>
                                    No items found to filter.
                                </div>
                            );
                        })()}
                    </div>
                    <small className="help-text" style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
                        Uncheck items to hide them from the chart.
                    </small>
                </div>
            )}

            {/* Filter/Group Configuration - Hide for Charts AND Tables */}
            {!isChartWidget(block.widgetType) && block.widgetType !== 'table' && (
                <div className="property-field" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                    <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                        Filter/Group Data
                    </label>

                    <div className="property-field" style={{ marginBottom: '0.75rem' }}>
                        <label style={{ fontSize: '0.875rem' }}>Mode</label>
                        <select
                            value={block.dataConfig.filterMode || 'none'}
                            onChange={(e) => {
                                updateDataConfig('filterMode', e.target.value);
                                if (e.target.value === 'none') {
                                    updateDataConfig('filterField', '');
                                    updateDataConfig('filterValue', '');
                                }
                            }}
                        >
                            <option value="none">No Filter/Group</option>
                            <option value="filter">Filter by Value</option>
                            <option value="group">Group by Field</option>
                        </select>
                        <small className="help-text" style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
                            Filter: Show only items matching a value. Group: Count items by field values.
                        </small>
                    </div>

                    {(block.dataConfig.filterMode === 'filter' || block.dataConfig.filterMode === 'group') && (
                        <>
                            <div className="property-field" style={{ marginBottom: '0.75rem' }}>
                                <label style={{ fontSize: '0.875rem' }}>Field to Filter/Group By</label>
                                <SearchableSelect
                                    value={block.dataConfig.filterField || ''}
                                    onChange={(newValue) => {
                                        updateDataConfig('filterField', newValue);
                                        updateDataConfig('filterValue', '');
                                    }}
                                    placeholder="Select a field..."
                                    options={(chartFieldData ? chartFieldData.availableFields : []).map(field => ({
                                        label: field,
                                        value: field
                                    }))}
                                />
                            </div>

                            {block.dataConfig.filterMode === 'filter' && block.dataConfig.filterField && (() => {
                                // Get unique values for the selected filter field
                                const data = getDataByPath(dataContext, block.dataConfig.metricPath);
                                const uniqueValues = [];
                                const valueSet = new Set();
                                if (data && Array.isArray(data)) {
                                    data.forEach(item => {
                                        if (item && typeof item === 'object' && block.dataConfig.filterField in item) {
                                            const val = item[block.dataConfig.filterField];
                                            const valStr = String(val);
                                            if (!valueSet.has(valStr)) {
                                                valueSet.add(valStr);
                                                uniqueValues.push(val);
                                            }
                                        }
                                    });
                                }

                                return (
                                    <div className="property-field">
                                        <label style={{ fontSize: '0.875rem' }}>Filter Value</label>
                                        <SearchableSelect
                                            value={block.dataConfig.filterValue || ''}
                                            onChange={(newValue) => updateDataConfig('filterValue', newValue)}
                                            placeholder="Select a value..."
                                            options={uniqueValues.map(val => {
                                                const valStr = String(val);
                                                const displayVal = typeof val === 'boolean' ? (val ? 'True' : 'False') : valStr;
                                                return { label: displayVal, value: valStr };
                                            })}
                                        />
                                        <small className="help-text" style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
                                            Show only items where "{block.dataConfig.filterField}" equals this value
                                        </small>
                                    </div>
                                );
                            })()}

                            {block.dataConfig.filterMode === 'group' && block.dataConfig.filterField && (
                                <div className="property-field" style={{
                                    padding: '0.75rem',
                                    backgroundColor: '#f0fdf4',
                                    borderRadius: '4px',
                                    border: '1px solid #86efac',
                                    fontSize: '0.875rem',
                                    color: '#166534'
                                }}>
                                    <strong>Group Mode:</strong> Chart will show counts grouped by "{block.dataConfig.filterField}" values.
                                    {(() => {
                                        const data = getDataByPath(dataContext, block.dataConfig.metricPath);
                                        const grouped = {};
                                        if (data && Array.isArray(data)) {
                                            data.forEach(item => {
                                                if (item && typeof item === 'object' && block.dataConfig.filterField in item) {
                                                    const val = String(item[block.dataConfig.filterField]);
                                                    grouped[val] = (grouped[val] || 0) + 1;
                                                }
                                            });
                                        }
                                        const groups = Object.entries(grouped);
                                        if (groups.length > 0) {
                                            return (
                                                <div style={{ marginTop: '0.5rem' }}>
                                                    <strong>Preview:</strong> {groups.map(([val, count]) =>
                                                        `${val}: ${count}`
                                                    ).join(', ')}
                                                </div>
                                            );
                                        }
                                        return null;
                                    })()}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Graph Type Recommendations */}
            {block.dataConfig.metricPath && (
                <div className="property-field" style={{
                    marginTop: '1rem',
                    padding: '0.75rem',
                    backgroundColor: '#f0f9ff',
                    borderRadius: '4px',
                    border: '1px solid #bae6fd'
                }}>
                    <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                        📊 Recommended Chart Types
                    </label>
                    <div style={{ fontSize: '0.875rem', color: '#1e40af' }}>
                        {block.dataConfig.filterMode === 'group' && (
                            <>
                                <div>• <strong>Pie/Donut:</strong> ✨ Best for showing proportions of grouped data</div>
                                <div>• <strong>Bar:</strong> Good for comparing counts across categories</div>
                            </>
                        )}
                        {/* Use memoized data if available, or just check general status */}
                        {block.dataConfig.filterMode !== 'group' && chartFieldData?.numericFields?.length > 0 && (
                            <>
                                <div>• <strong>Bar:</strong> Compare values across items</div>
                                <div>• <strong>Line:</strong> Show trends over items</div>
                                {chartFieldData.numericFields?.length >= 2 && (
                                    <div>• <strong>Scatter:</strong> Compare two numeric fields</div>
                                )}
                            </>
                        )}
                        {block.dataConfig.filterMode !== 'group' && (!chartFieldData || chartFieldData.numericFields?.length === 0) && (
                            <div>• <strong>Table:</strong> Best for displaying detailed records</div>
                        )}
                    </div>
                </div>
            )}

            {/* Only show aggregation for non-auto widgets and NOT for charts */}
            {block.widgetType !== 'table' && !isAutoWidget(block.widgetType) && !isChartWidget(block.widgetType) && (
                <div className="property-field">
                    <label>Aggregation</label>
                    <select
                        value={block.dataConfig.aggregation}
                        onChange={(e) => updateDataConfig('aggregation', e.target.value)}
                    >
                        <option value="count">Count</option>
                        <option value="sum">Sum</option>
                        <option value="average">Average</option>
                        <option value="min">Minimum</option>
                        <option value="max">Maximum</option>
                    </select>
                </div>
            )}

            {block.widgetType === 'table' && block.dataConfig.metricPath && (
                <TableColumnConfiguration
                    block={block}
                    scanData={scanData}
                    dataContext={dataContext}
                    onUpdateColumns={handleUpdateTableColumns}
                    onDataConfigUpdate={onDataConfigUpdate}
                    onFlattenFieldChange={(newFlattenField) => {
                        onDataConfigUpdate(block.id, {
                            ...block.dataConfig,
                            flattenField: newFlattenField,
                            tableColumns: block.dataConfig.tableColumns || []
                        });
                    }}
                />
            )}

            {/* Table Sorting - for standard, proof, and finding list tables */}
            {((block.widgetType === 'table' && block.dataConfig.metricPath) || 
             isFindingListWidget(block.widgetType) || 
             (!isChartWidget(block.widgetType) && block.widgetType !== 'table' && getExtractDataObj(block.widgetType))) && (
                <TableSortConfiguration
                    block={block}
                    onUpdateSort={handleUpdateTableSort}
                    extractDataObj={getExtractDataObj(block.widgetType)}
                    dataContext={dataContext}
                    columnLabels={TABLE_WIDGET_COLUMNS[block.widgetType]}
                    onDataConfigUpdate={onDataConfigUpdate}
                />
            )}

            {/* Table Filters - for custom table widgets */}
            {!isChartWidget(block.widgetType) && block.widgetType !== 'table' && getExtractDataObj(block.widgetType) && (
                <TableFiltersConfiguration
                    block={block}
                    dataContext={dataContext}
                    extractDataObj={getExtractDataObj(block.widgetType)}
                    onUpdateFilters={handleUpdateTableFilters}
                />
            )}

            {/* Pagination Configuration - Items Per Page for all paginated widgets */}
            {PAGINATED_WIDGETS.includes(block.widgetType) && (
                <div className="property-field" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                    <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>Pagination Settings</label>

                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
                            Items Per Page (First Page)
                        </label>
                        <select
                            value={block.dataConfig?.itemsPerPageFirstPage === 'auto' || block.dataConfig?.itemsPerPageFirstPage === undefined ? 'auto' : 'custom'}
                            onChange={(e) => {
                                if (e.target.value === 'auto') {
                                    updateDataConfig('itemsPerPageFirstPage', 'auto');
                                } else {
                                    const defaultVal = block.pagination?.itemsPerPageFirstPage || block.dataConfig?.itemsPerPage || block.pagination?.itemsPerPage || getDefaultItemsPerPage(block.widgetType);
                                    updateDataConfig('itemsPerPageFirstPage', typeof defaultVal === 'number' ? defaultVal : getDefaultItemsPerPage(block.widgetType));
                                }
                            }}
                            style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                        >
                            <option value="auto">Auto (Calculate from widget height)</option>
                            <option value="custom">Custom number...</option>
                        </select>
                        {block.dataConfig?.itemsPerPageFirstPage !== 'auto' && block.dataConfig?.itemsPerPageFirstPage !== undefined && (
                            <input
                                type="number"
                                min="1"
                                max="100"
                                value={typeof block.dataConfig?.itemsPerPageFirstPage === 'number'
                                    ? block.dataConfig.itemsPerPageFirstPage
                                    : block.pagination?.itemsPerPageFirstPage || block.dataConfig?.itemsPerPage || block.pagination?.itemsPerPage || getDefaultItemsPerPage(block.widgetType)}
                                onChange={(e) => {
                                    const itemsPerPageFirstPage = parseInt(e.target.value) || 1;
                                    updateDataConfig('itemsPerPageFirstPage', itemsPerPageFirstPage);
                                }}
                                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                            />
                        )}
                        <small className="help-text" style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
                            Number of items on the first page (table may start mid-page, so fewer items fit)
                        </small>
                    </div>

                    <div>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
                            Items Per Page (Continuation Pages)
                        </label>
                        <select
                            value={block.dataConfig?.itemsPerPageContinuation === 'auto' || block.dataConfig?.itemsPerPageContinuation === undefined ? 'auto' : 'custom'}
                            onChange={(e) => {
                                if (e.target.value === 'auto') {
                                    updateDataConfig('itemsPerPageContinuation', 'auto');
                                } else {
                                    const defaultVal = block.pagination?.itemsPerPageContinuation || block.dataConfig?.itemsPerPageFirstPage || block.pagination?.itemsPerPageFirstPage || block.dataConfig?.itemsPerPage || block.pagination?.itemsPerPage || getDefaultItemsPerPage(block.widgetType);
                                    updateDataConfig('itemsPerPageContinuation', typeof defaultVal === 'number' ? defaultVal : getDefaultItemsPerPage(block.widgetType));
                                }
                            }}
                            style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                        >
                            <option value="auto">Auto (Calculate from page height)</option>
                            <option value="custom">Custom number...</option>
                        </select>
                        {block.dataConfig?.itemsPerPageContinuation !== 'auto' && block.dataConfig?.itemsPerPageContinuation !== undefined && (
                            <input
                                type="number"
                                min="1"
                                max="100"
                                value={typeof block.dataConfig?.itemsPerPageContinuation === 'number'
                                    ? block.dataConfig.itemsPerPageContinuation
                                    : block.pagination?.itemsPerPageContinuation || block.dataConfig?.itemsPerPageFirstPage || block.pagination?.itemsPerPageFirstPage || block.dataConfig?.itemsPerPage || block.pagination?.itemsPerPage || getDefaultItemsPerPage(block.widgetType)}
                                onChange={(e) => {
                                    const itemsPerPageContinuation = parseInt(e.target.value) || 1;
                                    updateDataConfig('itemsPerPageContinuation', itemsPerPageContinuation);
                                }}
                                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                            />
                        )}
                        <small className="help-text" style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
                            Number of items on continuation pages (start from top of page, so more items fit)
                        </small>
                    </div>
                </div>
            )}

            {/* Software Inventory Specific Configuration */}
            {block.widgetType === 'software-inventory-table' && (
                <div className="property-field" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                    <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                        ⚙️ Application Filters & Grouping
                    </label>

                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <input
                            type="checkbox"
                            checked={block.config?.groupIdentical || false}
                            onChange={(e) => updateWidgetConfig('groupIdentical', e.target.checked)}
                            style={{ marginRight: '0.5rem', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.9rem', color: '#333' }}>
                            Group identical software across hosts (show hostnames list)
                        </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <input
                            type="checkbox"
                            checked={block.config?.hideMicrosoft || false}
                            onChange={(e) => updateWidgetConfig('hideMicrosoft', e.target.checked)}
                            style={{ marginRight: '0.5rem', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.9rem', color: '#333' }}>
                            Hide Microsoft/Windows components (reduce noise)
                        </span>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
                            Show only software installed on X+ hosts (Threshold)
                        </label>
                        <input
                            type="number"
                            min="1"
                            value={block.config?.hostThreshold || 1}
                            onChange={(e) => {
                                const threshold = parseInt(e.target.value) || 1;
                                updateWidgetConfig('hostThreshold', threshold);
                            }}
                            style={{ width: '100%', padding: '0.5rem' }}
                        />
                        <small className="help-text" style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
                            Requires 1 or more hosts. Useful for finding widespread software.
                        </small>
                    </div>
                </div>
            )}

            {/* Local Administrators Specific Configuration */}
            {block.widgetType === 'local-administrators-table' && (
                <div className="property-field" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                    <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                        ⚙️ Administrator Filters
                    </label>

                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <input
                            type="checkbox"
                            checked={block.config?.showEmptyDevices || false}
                            onChange={(e) => updateWidgetConfig('showEmptyDevices', e.target.checked)}
                            style={{ marginRight: '0.5rem', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.9rem', color: '#333' }}>
                            Show devices even if they have no local administrators
                        </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <input
                            type="checkbox"
                            checked={block.config?.showStatusIcon !== false}
                            onChange={(e) => updateWidgetConfig('showStatusIcon', e.target.checked)}
                            style={{ marginRight: '0.5rem', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.9rem', color: '#333' }}>
                            Show status indicators (colored circles)
                        </span>
                    </div>
                </div>
            )}

            {/* Workstation Age Specific Configuration */}
            {block.widgetType === 'ad-workstation-age-table' && (
                <div className="property-field" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                    <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                        ⚙️ Workstation Age Filters
                    </label>

                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
                            Show only workstations older than X months (Threshold)
                        </label>
                        <input
                            type="number"
                            min="0"
                            value={block.config?.ageThreshold !== undefined ? block.config.ageThreshold : 0}
                            onChange={(e) => {
                                const threshold = parseInt(e.target.value) || 0;
                                updateWidgetConfig('ageThreshold', threshold);
                            }}
                            style={{ width: '100%', padding: '0.5rem' }}
                        />
                        <small className="help-text" style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
                            Enter 0 to show all workstations. Useful for filtering out newer devices.
                        </small>
                    </div>
                </div>
            )}

            {/* AD Admins Specific Configuration */}
            {block.widgetType === 'ad-admins-table' && (() => {
                // AD Admin Filter → Table Column Filter sync mapping
                const AD_FILTER_MAP = {
                    showOnlyEnabled:  { fieldPath: 'Status', operator: 'contains', value: 'Enabled' },
                    showOnlyDisabled: { fieldPath: 'Status', operator: 'equals',   value: 'Disabled' },
                    showOnlyUsers:    { fieldPath: 'Type',   operator: 'equals',   value: 'User' },
                    showOnlyGroups:   { fieldPath: 'Type',   operator: 'equals',   value: 'Group' },
                };

                // When a checkbox is toggled, update both widget config AND sync to table filters
                const handleAdFilterToggle = (filterKey, checked) => {
                    // 1. Update the widget config checkbox
                    updateWidgetConfig(filterKey, checked);

                    // 2. Sync to Table Column Filters
                    const rawTableFilters = block.dataConfig?.tableFilters;
                    const existingFilters = Array.isArray(rawTableFilters) ? rawTableFilters : (rawTableFilters?.filters || []);
                    const existingCombinator = rawTableFilters?.combinator || 'AND';

                    // Remove any existing auto-generated filter for this key
                    let updatedFilters = existingFilters.filter(f => f.source !== `ad-admin-${filterKey}`);

                    // If toggled ON, add the corresponding filter
                    if (checked) {
                        const mapping = AD_FILTER_MAP[filterKey];
                        if (mapping) {
                            updatedFilters.push({
                                id: `ad-admin-${filterKey}-${Date.now()}`,
                                fieldPath: mapping.fieldPath,
                                operator: mapping.operator,
                                value: mapping.value,
                                source: `ad-admin-${filterKey}`
                            });
                        }
                    }

                    // Emit the updated filters
                    handleUpdateTableFilters({
                        filters: updatedFilters,
                        combinator: existingCombinator
                    });
                };

                return (
                <div className="property-field" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                    <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                        ⚙️ AD Admin Filters
                    </label>
                    <small className="help-text" style={{ display: 'block', marginBottom: '0.5rem', color: '#666' }}>
                        These filters also appear as Table Column Filters below.
                    </small>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <input
                                type="checkbox"
                                checked={block.config?.showOnlyEnabled || false}
                                onChange={(e) => handleAdFilterToggle('showOnlyEnabled', e.target.checked)}
                                style={{ marginRight: '0.4rem', cursor: 'pointer' }}
                            />
                            <span style={{ fontSize: '0.85rem' }}>Only Enabled</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <input
                                type="checkbox"
                                checked={block.config?.showOnlyDisabled || false}
                                onChange={(e) => handleAdFilterToggle('showOnlyDisabled', e.target.checked)}
                                style={{ marginRight: '0.4rem', cursor: 'pointer' }}
                            />
                            <span style={{ fontSize: '0.85rem' }}>Only Disabled</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <input
                                type="checkbox"
                                checked={block.config?.showOnlyUsers || false}
                                onChange={(e) => handleAdFilterToggle('showOnlyUsers', e.target.checked)}
                                style={{ marginRight: '0.4rem', cursor: 'pointer' }}
                            />
                            <span style={{ fontSize: '0.85rem' }}>Only Users</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <input
                                type="checkbox"
                                checked={block.config?.showOnlyGroups || false}
                                onChange={(e) => handleAdFilterToggle('showOnlyGroups', e.target.checked)}
                                style={{ marginRight: '0.4rem', cursor: 'pointer' }}
                            />
                            <span style={{ fontSize: '0.85rem' }}>Only Groups</span>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <input
                            type="checkbox"
                            checked={block.config?.showStatusIcon !== false}
                            onChange={(e) => updateWidgetConfig('showStatusIcon', e.target.checked)}
                            style={{ marginRight: '0.5rem', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.9rem', color: '#333' }}>
                            Show severity status indicators
                        </span>
                    </div>
                </div>
                );
            })()}

            {/* Column Visibility - for non-standard table widgets */}
            {(() => {
                const columns = TABLE_WIDGET_COLUMNS[block.widgetType];
                if (!columns || columns.length === 0) return null;

                const hiddenColumns = block.config?.hiddenColumns || [];
                const customColumnHeaders = block.config?.customColumnHeaders || {};

                return (
                    <div className="property-field" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                        <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                            📋 Column Visibility & Headers
                        </label>
                        <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '0.5rem' }}>
                            {columns.map((col) => {
                                const isHidden = hiddenColumns.includes(col);
                                const customName = customColumnHeaders[col] || '';
                                return (
                                    <div key={col} style={{ marginBottom: '0.35rem', padding: '0.25rem 0.5rem', borderRadius: '4px', backgroundColor: isHidden ? 'rgba(239, 68, 68, 0.05)' : 'rgba(34, 197, 94, 0.05)' }}>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <input
                                                type="checkbox"
                                                checked={!isHidden}
                                                onChange={(e) => {
                                                    const checked = e.target.checked;
                                                    let newHiddenColumns;
                                                    if (!checked) {
                                                        newHiddenColumns = [...hiddenColumns, col];
                                                    } else {
                                                        newHiddenColumns = hiddenColumns.filter(c => c !== col);
                                                    }
                                                    updateWidgetConfig('hiddenColumns', newHiddenColumns);
                                                }}
                                                style={{ marginRight: '0.5rem', cursor: 'pointer' }}
                                            />
                                            <span style={{ fontSize: '0.9rem', color: isHidden ? '#999' : '#333', textDecoration: isHidden ? 'line-through' : 'none', minWidth: '80px' }}>
                                                {col}
                                            </span>
                                        </div>
                                        {!isHidden && (
                                            <div style={{ display: 'flex', alignItems: 'center', marginLeft: '1.75rem', marginTop: '0.2rem' }}>
                                                <input
                                                    type="text"
                                                    placeholder={col}
                                                    value={customName}
                                                    onChange={(e) => {
                                                        const newCustomHeaders = { ...customColumnHeaders };
                                                        if (e.target.value === '' || e.target.value === col) {
                                                            delete newCustomHeaders[col];
                                                        } else {
                                                            newCustomHeaders[col] = e.target.value;
                                                        }
                                                        updateWidgetConfig('customColumnHeaders', newCustomHeaders);
                                                    }}
                                                    style={{
                                                        flex: 1,
                                                        padding: '0.2rem 0.4rem',
                                                        fontSize: '0.8rem',
                                                        border: '1px solid #d1d5db',
                                                        borderRadius: '4px',
                                                        outline: 'none',
                                                        color: customName ? '#1e40af' : '#9ca3af'
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <small className="help-text" style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
                            Uncheck to hide columns. Type in the text field to rename the header.
                        </small>
                    </div>
                );
            })()}

            {/* Device Filters - available for widgets with array data source */}
            {(block.dataConfig.metricPath || block.dataConfig.browserSelection?.path) && (
                <DeviceFiltersConfiguration
                    block={block}
                    scanData={scanData}
                    dataContext={dataContext}
                    onUpdateFilters={handleUpdateDeviceFilters}
                />
            )}

            {/* Unit Cost Editor for Cost Summary Widgets */}
            {isCostSummaryWidget(block.widgetType) && (
                <div className="property-field" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                    <label>Unit Cost</label>
                    <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={block.dataConfig?.customUnitCosts?.[getCostSummaryItemName(block.widgetType)] ?? getDefaultUnitCost(block.widgetType)}
                        onChange={(e) => {
                            const newUnitCost = parseFloat(e.target.value) || 0;
                            const itemName = getCostSummaryItemName(block.widgetType);
                            if (itemName) {
                                updateDataConfig('customUnitCosts', { [itemName]: newUnitCost });
                            }
                        }}
                        style={{ width: '100%', padding: '0.5rem' }}
                    />
                    <small className="help-text" style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
                        Cost per item for {getCostSummaryItemName(block.widgetType)} findings. Changing this will update the Item Cost and Total Cost Calculator.
                    </small>
                </div>
            )}

            {/* Page Range Configuration for Header and Footer Elements */}
            {(block.type === 'element' && (block.elementType === 'header' || block.elementType === 'footer')) && (
                <div className="property-field" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                    <label>From Page</label>
                    <input
                        type="number"
                        min="1"
                        value={block.dataConfig?.fromPage ?? block.dataConfig?.startPage ?? block.config?.fromPage ?? block.config?.startPage ?? 1}
                        onChange={(e) => {
                            const fromPage = parseInt(e.target.value) || 1;
                            updateDataConfig('fromPage', fromPage);
                            // Remove old startPage if it exists
                            if (block.dataConfig?.startPage) {
                                const newDataConfig = { ...block.dataConfig };
                                delete newDataConfig.startPage;
                                updateDataConfig('startPage', undefined);
                            }
                        }}
                        style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem' }}
                    />
                    <label style={{ marginTop: '0.5rem', display: 'block' }}>To Page</label>
                    <select
                        value={block.dataConfig?.toPage === 'last' || !block.dataConfig?.toPage ? 'last' : String(block.dataConfig.toPage)}
                        onChange={(e) => {
                            const toPage = e.target.value === 'last' ? 'last' : parseInt(e.target.value) || 'last';
                            updateDataConfig('toPage', toPage);
                        }}
                        style={{ width: '100%', padding: '0.5rem' }}
                    >
                        <option value="last">Last Page</option>
                        {pages.map((_, index) => (
                            <option key={index + 1} value={index + 1}>
                                Page {index + 1}
                            </option>
                        ))}
                    </select>
                    <small className="help-text" style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
                        The {block.elementType === 'header' ? 'header' : 'footer'} will appear on pages from the specified range (From Page to To Page). Select "Last Page" to show until the end of the report.
                    </small>
                </div>
            )}

        </div>
    );
};

export default DataTab;
